import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useCanvasPopoverEntry,
  useOnboardingPreferencesStore,
  useShowFirstUploadPromptValue,
} from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useURLClipIdSelector } from '~/hooks/useURLClipIdSelector';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useListCanvases } from '~/swr-hooks/canvases/useListCanvases';
import { isCypressRun } from '~/utils/PathUtils';
import { canCreateCanvasInWorkspace, canEditCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';

// Reduce popover delay on cypress to speed up tests
const DEFAULT_POPOVER_DELAY = 5000;
const CYPRESS_POPOVER_DELAY = 100;
const POPOVER_DELAY = isCypressRun() ? CYPRESS_POPOVER_DELAY : DEFAULT_POPOVER_DELAY;

const POPOVER_ALLOWED_ROUTES = [
  Pathname.Search,
  '/b/',
  '/l/',
  Pathname.SavedSearch,
  Routes.media.recentlyAdded,
  Routes.media.myUploads,
  Routes.media.favoriteItems,
  Routes.media.recentlyModified,
  Routes.media.unusedItems,
  Routes.media.untaggedItems,
];

export const useTryCanvasPopoverState = () => {
  const router = useRouter();
  const { currentWorkspace } = useCurrentWorkspace();
  const boardId = useURLBoardIdSelector();
  const clipId = useURLClipIdSelector();
  const workspaceId = currentWorkspace?.id;
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const canvasEntry = useCanvasPopoverEntry(workspaceId);
  const markCanvasPopoverShown = useOnboardingPreferencesStore((s) => s.markCanvasPopoverShown);
  const dismissCanvasPopover = useOnboardingPreferencesStore((s) => s.dismissCanvasPopover);
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();

  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isInAssetModalRoute = !!clipId;

  const canEditInWorkspace =
    canEditCanvasInWorkspace(workspacePermissions) && canCreateCanvasInWorkspace(workspacePermissions);

  const { total: totalUserCanvases, isLoading: isCanvasesLoading } = useListCanvases({
    workspaceId,
    view: 'mine',
    limit: 1,
    includeInactive: true,
  });

  // Dismiss if user already has canvases
  useEffect(() => {
    if (totalUserCanvases > 0 && workspaceId) {
      setVisible(false);
      dismissCanvasPopover(workspaceId);
    }
  }, [totalUserCanvases, workspaceId, dismissCanvasPopover]);

  useEffect(() => {
    if (canvasEntry?.status === 'dismissed') {
      setVisible(false);
    }
  }, [canvasEntry?.status]);

  const isInOnboardingFlow = useShowFirstUploadPromptValue(workspaceId);
  const isOnAllowedRoute = POPOVER_ALLOWED_ROUTES.some((route) => router.pathname.startsWith(route));

  const shouldStartTimer =
    generativeCanvasEnabled &&
    canEditInWorkspace &&
    !isCanvasesLoading &&
    totalUserCanvases === 0 &&
    !isInOnboardingFlow &&
    !isInAssetModalRoute &&
    isOnAllowedRoute &&
    canvasEntry === undefined;

  useEffect(() => {
    if (shouldStartTimer) {
      timeoutRef.current = setTimeout(() => {
        setVisible(true);
        if (workspaceId) {
          markCanvasPopoverShown(workspaceId);
        }
      }, POPOVER_DELAY);
    } else if (!visible) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [shouldStartTimer, workspaceId, markCanvasPopoverShown, visible]);

  const handleClose = useCallback(() => {
    setVisible(false);
    if (workspaceId) {
      dismissCanvasPopover(workspaceId);
    }
  }, [dismissCanvasPopover, workspaceId]);

  const isVisible = visible && generativeCanvasEnabled && isOnAllowedRoute && !isInAssetModalRoute;

  return { visible: isVisible, boardId, handleClose };
};
